import React, { useEffect, useMemo } from "react";

import {
  Building2,
  Calendar,
  CheckCircle,
  ChevronRight,
  Clock,
  Home,
  Info,
  TrendingUp,
  User,
  UserRound,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import DailyPlaybookModal from "../../Components/GuidancePlaybooks/DailyPlaybookModal";
import TodaysPlaybookWidget from "../../Components/GuidancePlaybooks/TodaysPlaybookWidget";
import HomeVideosSection from "../../Components/HomeVideosSection";
import PageHeader from "../../Components/PageHeader";
import WelcomeBanner from "../../Components/WelcomeBanner";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchReviewAssessments,
  ReviewAssessment,
} from "../../Store/Reducers/AssessmentSlice";
import { fetchAssignedAccounts } from "../../Store/Reducers/CustomerWorkspaceSlice";
import { fetchDashboardCounts } from "../../Store/Reducers/MasterSlice";
import { fetchUserVideos } from "../../Store/Reducers/VideoSlice";
import {
  ROLE_DISPLAY_NAMES,
  normalizeRoleName,
} from "../../Utils/Constants/RoleConstants";
import { isGuidanceHubActiveForUser } from "../../Utils/Guidance/guidanceHubSettings";
import {
  getTierBadgeClasses,
  useAccountTiers,
} from "../../Utils/Helper/accountTierHelpers";
import { formatAssessmentDateTimePartsUtc } from "../../Utils/Helper/assessmentDateUtils";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";

import "./style.scss";

interface AssignedAccount {
  id: string;
  name: string;
  description: string;
  tier: string;
}

interface PendingAssessment {
  id: string;
  title: string;
  dueDate: string;
  assignedBy: string;
  assignedDate: string;
  accountId: string;
  accountName: string;
  accountTier: string;
}

const AccountTeamHome: React.FC = () => {
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const dispatch = useAppDispatch();
  const { formatTier } = useAccountTiers();

  const withOrg = (path: string) => {
    const p = path.startsWith("/") ? path : `/${path}`;
    return orgSlug ? `/${orgSlug}${p}` : p;
  };

  const { user } = useAppSelector(state => state.authentication);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const { reviewAssessments, reviewAssessmentsLoading, reviewAssessmentsMeta } =
    useAppSelector(state => state.assessment);
  const { dashboardCounts } = useAppSelector(state => state.master);
  const { assignedAccounts } = useAppSelector(state => state.customerWorkspace);
  const { userVideos, userVideosLoading } = useAppSelector(
    (state: any) => state.video
  );
  const { hubEnabled, hubEnabledRoles } = useAppSelector(
    state => state.guidance
  );

  const userName = loggedInUser?.fullName || user?.user?.full_name || "User";
  const organization = loggedInUser?.tenantName || user?.user?.tenant_domain;
  const userRole = user?.user?.role || loggedInUser?.role;
  const hubActive = isGuidanceHubActiveForUser(
    hubEnabled,
    hubEnabledRoles,
    userRole
  );
  const normalizedRole = normalizeRoleName(userRole);
  const baseRoleDisplayName =
    normalizedRole && normalizedRole in ROLE_DISPLAY_NAMES
      ? ROLE_DISPLAY_NAMES[normalizedRole as keyof typeof ROLE_DISPLAY_NAMES]
      : "Account Team";

  const executiveId = loggedInUser?.userId ? String(loggedInUser.userId) : "";

  useEffect(() => {
    if (executiveId) {
      dispatch(
        fetchReviewAssessments({
          executive_id: executiveId,
          tab: "assigned",
        })
      );
    }
    dispatch(fetchDashboardCounts());
    dispatch(fetchAssignedAccounts({}));

    if (normalizedRole) {
      dispatch(fetchUserVideos({ role: normalizedRole }) as any);
    }
  }, [dispatch, executiveId, normalizedRole]);

  const videos = useMemo(
    () => (userVideos && userVideos.length > 0 ? userVideos : []),
    [userVideos]
  );
  const hasVideos = videos.length > 0;

  const pendingAssessmentsCount = reviewAssessmentsMeta?.total_count || 0;
  const assignedAccountsCount =
    assignedAccounts.total || dashboardCounts.data?.assigned_accounts || 0;
  const roleDisplayName = `${baseRoleDisplayName} • ${assignedAccountsCount} accounts assigned`;

  const programKPIs = [
    {
      title: "Portfolio Accounts",
      value: assignedAccountsCount,
      colorClass: "text-purple-600",
      bgColorClass: "bg-purple-50",
    },
    {
      title: "Pending Assessments",
      value: pendingAssessmentsCount,
      colorClass: "text-red-600",
      bgColorClass: "bg-red-50",
    },
    {
      title: "Completed Assessments",
      value: dashboardCounts.data?.completed_assessments ?? 0,
      colorClass: "text-orange-600",
      bgColorClass: "bg-orange-50",
    },
  ];

  const assignedAccountsList: AssignedAccount[] = useMemo(() => {
    if (!assignedAccounts.items?.length) return [];

    return assignedAccounts.items.map(account => ({
      id: account.customer_id,
      name: account.customer_name,
      description: account.industry || "Not specified",
      tier: account.tier || "N/A",
    }));
  }, [assignedAccounts.items]);

  const pendingAssessments: PendingAssessment[] = useMemo(() => {
    if (!reviewAssessments?.length) return [];

    const assessments: PendingAssessment[] = [];

    reviewAssessments.forEach((assessment: ReviewAssessment) => {
      const dueDateFormatted = formatAssessmentDateTimePartsUtc(
        assessment.due_date,
        "DMY_SHORT"
      ).date;
      const assignedDateFormatted = formatAssessmentDateTimePartsUtc(
        assessment.created_at,
        "DMY_SHORT"
      ).date;

      if (assessment.accounts?.length) {
        assessment.accounts.forEach(account => {
          assessments.push({
            id: `${assessment.assessment_id}-${account.account_id}`,
            title: assessment.assessment_name,
            dueDate: dueDateFormatted,
            assignedBy: assessment.created_by,
            assignedDate: assignedDateFormatted,
            accountId: account.account_id,
            accountName: account.account_name,
            accountTier: account.account_tier || "N/A",
          });
        });
      } else {
        assessments.push({
          id: assessment.assessment_id,
          title: assessment.assessment_name,
          dueDate: dueDateFormatted,
          assignedBy: assessment.created_by,
          assignedDate: assignedDateFormatted,
          accountId: "",
          accountName: "No Account",
          accountTier: "N/A",
        });
      }
    });

    return assessments;
  }, [reviewAssessments]);

  return (
    <div className="bg-white overflow-hidden mt-3">
      <PageHeader title="Home" icon={Home} />
      <div className="w-full bg-white p-3 md:p-4">
        <div className="rounded-md bg-gray-100 p-4 md:p-4">
          <div className="account-team-home">
            <WelcomeBanner
              userName={userName}
              roleDisplayName={roleDisplayName}
              organization={organization}
            />

            <DailyPlaybookModal userRole={userRole} />

            <div
              className={`grid grid-cols-1 ${
                hasVideos ? "lg:grid-cols-2" : "lg:grid-cols-1"
              } gap-6 mb-6`}
            >
              <div className="account-team-home__section bg-white rounded-lg p-6 border border-gray-200">
                <div className="mb-5">
                  <h2 className="text-xl font-semibold text-gray-900 mb-1 tracking-tight">
                    Account Overview
                  </h2>
                  <p className="text-sm text-gray-500">
                    Your account-team KPIs at a glance
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {programKPIs.map((kpi, index) => (
                    <div
                      key={index}
                      className={`${kpi.bgColorClass} rounded-xl p-4 min-h-[72px]`}
                    >
                      <div
                        className={`text-xs sm:text-sm font-medium ${kpi.colorClass}`}
                      >
                        {kpi.title}
                      </div>
                      <div
                        className={`text-xl sm:text-2xl font-semibold tabular-nums ${kpi.colorClass}`}
                      >
                        {kpi.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {hasVideos && (
                <HomeVideosSection
                  videos={videos}
                  loading={userVideosLoading}
                  sectionClassName="account-team-home__section bg-white rounded-lg p-6 border border-gray-200 h-fit"
                  videoWrapClassName="account-team-home__video-wrap relative w-full h-[240px] sm:h-[280px] rounded-2xl overflow-hidden bg-gray-100 mb-3"
                  placeholderClassName="account-team-home__video-wrap w-full h-[240px] sm:h-[280px] rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-3 text-gray-500"
                  showDescription={true}
                />
              )}
            </div>

            <div
              className={`grid grid-cols-1 ${
                hubActive ? "lg:grid-cols-2" : ""
              } gap-6 mb-6`}
            >
              <div className="account-team-home__section bg-white rounded-lg p-6 border border-gray-200 h-full">
                <div className="mb-5">
                  <h2 className="text-xl font-semibold text-gray-900 mb-1 tracking-tight">
                    Recent Accounts
                  </h2>
                  <p className="text-sm text-gray-500">
                    Accounts you support on the team
                  </p>
                </div>
                {assignedAccounts.loading ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <div className="animate-spin rounded-full h-9 w-9 border-2 border-gray-200 border-t-purple-600" />
                    <span className="mt-3 text-sm font-medium">
                      Loading recent accounts…
                    </span>
                  </div>
                ) : assignedAccountsList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <Building2
                      className="w-12 h-12 text-gray-300 mb-3"
                      strokeWidth={1.5}
                    />
                    <span className="text-sm font-medium">
                      No recent accounts
                    </span>
                  </div>
                ) : (
                  <div
                    className={`account-team-home__accounts-grid thin-scrollbar-y grid ${
                      hubActive
                        ? "account-team-home__accounts-grid--compact grid-cols-2 xl:grid-cols-3"
                        : "account-team-home__accounts-grid--full grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
                    }`}
                  >
                    {assignedAccountsList.map(account => (
                      <div
                        key={account.id}
                        onClick={() =>
                          navigate(withOrg(`/account-synopsis/${account.id}`), {
                            state: {
                              from: withOrg("/home"),
                              fromLabel: "Home",
                            },
                          })
                        }
                        className={`account-team-home__card-clickable bg-white border border-gray-200 rounded-xl cursor-pointer ${
                          hubActive ? "p-3" : "p-4"
                        }`}
                      >
                        <h3
                          className={`font-semibold text-gray-900 mb-1 truncate ${
                            hubActive ? "text-sm" : "text-base"
                          }`}
                        >
                          {account.name}
                        </h3>
                        <p
                          className={`text-gray-500 line-clamp-2 ${
                            hubActive ? "text-xs mb-2" : "text-sm"
                          }`}
                        >
                          {account.description}
                        </p>
                        <span
                          className={`inline-block text-xs font-semibold rounded-md ${getTierBadgeClasses(account.tier)} ${
                            hubActive ? "px-2 py-0.5" : "px-2.5 py-1"
                          }`}
                        >
                          {formatTier(account.tier) || account.tier || "N/A"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {hubActive && (
                <TodaysPlaybookWidget userRole={userRole} className="mb-0" />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div
                onClick={() => navigate(withOrg("/always-on-channel"))}
                className="account-team-home__card-clickable group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-purple-100 rounded-lg p-3">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900">
                      Always On Intelligence
                    </h3>
                    <p className="text-sm text-gray-500">
                      View real-time insights
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto" />
                </div>
              </div>

              <div
                onClick={() => navigate(withOrg("/pending-assessments"))}
                className="account-team-home__card-clickable group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 rounded-lg p-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900">
                      My Assessments
                    </h3>
                    <p className="text-sm text-gray-500">
                      Complete pending assessments
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto" />
                </div>
              </div>

              <div
                onClick={() => navigate(withOrg("/goals-milestones"))}
                className="account-team-home__card-clickable group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 rounded-lg p-3">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900">
                      My Progress
                    </h3>
                    <p className="text-sm text-gray-500">
                      Track your milestones
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto" />
                </div>
              </div>
            </div>

            <div className="account-team-home__section bg-white rounded-lg p-6 border border-gray-200">
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 tracking-tight">
                    Pending Assessments
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Assessments awaiting your review
                  </p>
                </div>
                {pendingAssessments.length > 0 && (
                  <button
                    type="button"
                    onClick={() => navigate(withOrg("/pending-assessments"))}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-purple-600 hover:text-purple-700 rounded"
                  >
                    See all
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
              {reviewAssessmentsLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <div className="animate-spin rounded-full h-9 w-9 border-2 border-gray-200 border-t-purple-600" />
                  <span className="mt-3 text-sm font-medium">
                    Loading assessments…
                  </span>
                </div>
              ) : pendingAssessments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <CheckCircle
                    className="w-12 h-12 text-gray-300 mb-3"
                    strokeWidth={1.5}
                  />
                  <span className="text-sm font-medium">
                    No pending assessments
                  </span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {pendingAssessments.slice(0, 4).map(assessment => (
                    <div
                      key={assessment.id}
                      onClick={() => navigate(withOrg("/pending-assessments"))}
                      className="account-team-home__card-clickable bg-white border border-gray-200 rounded-lg p-4 cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="bg-purple-100 rounded-lg p-2 shrink-0">
                            <Info className="w-4 h-4 text-purple-600" />
                          </div>
                          <span className="text-sm font-semibold text-gray-900 line-clamp-2">
                            {assessment.title}
                          </span>
                        </div>
                        <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-2.5 py-1 rounded-full shrink-0">
                          Pending
                        </span>
                      </div>
                      <div className="mb-3 flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                        <span className="text-sm font-medium text-blue-600 truncate">
                          {assessment.accountName}
                        </span>
                      </div>
                      <div className="space-y-2 text-xs text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5 shrink-0" />
                          <span>Due: {assessment.dueDate}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <UserRound className="w-3.5 h-3.5 shrink-0" />
                          <span>By: {assessment.assignedBy}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 shrink-0" />
                          <span>Assigned: {assessment.assignedDate}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountTeamHome;
